import * as React from 'react';
import { Box, Button, Heading, Text, VStack } from '@chakra-ui/react';
import { Icon } from '../../../shared/Icon';

interface IProps {
  children: React.ReactNode;
  onReset?: () => void;
}

interface IState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary specifically for scoreboard components.
 * Provides a lightweight fallback UI that allows recovery without full page reload.
 */
export class ScoreboardErrorBoundary extends React.Component<IProps, IState> {
  public constructor(props: IProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  public static getDerivedStateFromError(error: Error): IState {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log error to tracking service if available
    if (window['TrackJS']) {
      window['TrackJS'].track(error);
    }
    console.error('[ScoreboardErrorBoundary] Caught error:', error, errorInfo);
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, error: null });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  public render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <Box
          display='flex'
          alignItems='center'
          justifyContent='center'
          height='100%'
          width='100%'
          bg='gray.50'
          p={8}
        >
          <VStack gap={4} maxW='md' textAlign='center'>
            <Icon
              name='error'
              style={{ fontSize: '64px', color: '#f56565' }}
            />
            <Heading size='lg'>Scoreboard Error</Heading>
            <Text color='gray.600'>
              Something went wrong while displaying the scoreboard. This error
              has been logged.
            </Text>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Box
                bg='red.50'
                p={3}
                borderRadius='md'
                fontSize='sm'
                fontFamily='mono'
                color='red.700'
                maxW='100%'
                overflow='auto'
              >
                {this.state.error.message}
              </Box>
            )}
            <Button colorScheme='blue' onClick={this.handleReset}>
              <Icon name='refresh' style={{ marginRight: '8px' }} />
              Try Again
            </Button>
          </VStack>
        </Box>
      );
    }

    return this.props.children;
  }
}
